import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribes to a CSS media query.
 *
 * <p>`useSyncExternalStore` rather than `useState` + an effect: the store reads the
 * current value during render, so the first paint is already correct. The effect version
 * renders once with a guessed value and then corrects itself, which on a layout switch
 * (a floating card becoming a bottom sheet) is a visible flash and, worse, mounts the
 * wrong component for a frame.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const media = window.matchMedia(query);
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    // Server snapshot: assume desktop. There is no SSR here, but the signature needs it
    // and a wrong guess would only matter for a hydration pass that never happens.
    () => false,
  );
}

/** Tailwind's `md`. The breakpoint where the detail panel stops floating and becomes a sheet. */
export const MD_UP = '(min-width: 768px)';
